/**
 * F-148 §10.2 `/stock/receiving/receipts/:receiptId` (AC20–AC26): immutable receipt detail,
 * discrepancy download and bounded per-line corrections. Corrections stay available while the
 * store's receiving flag is off (§6.3).
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import { useCorrectReceiptLine, useReceivingAccess, useStockReceipt } from '@dukanti/shared/hooks';
import type { CorrectReceiptLineInput, StockReceiptLine } from '@dukanti/shared/admin';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import { Button } from '@/components/ui/button';
import {
  CorrectionDialog,
  ReceiptDetail,
  ReceivingNotAvailable,
  ReceivingPausedBanner,
} from '@/features/stock/receiving/components';
import {
  receivingErrorKey,
  receivingErrorOptions,
} from '@/features/stock/receiving/lib/error-messages';

export function StockReceiptRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const { receiptId = null } = useParams<{ receiptId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const access = useReceivingAccess(supabase, storeId);
  const receipt = useStockReceipt(supabase, canManageOf(access.data) ? receiptId : null);
  const correct = useCorrectReceiptLine(supabase);

  const canManage = canManageOf(access.data);
  const enabled = access.data?.enabled ?? false;
  const [correctionLine, setCorrectionLine] = useState<StockReceiptLine | null>(null);
  const [correctionError, setCorrectionError] = useState<unknown>(null);
  // `dataUpdatedAt` of the receipt a failed correction was started from. A failure can be an
  // ambiguous commit (the server applied it, the response was lost), so the loaded receipt is not
  // trusted for a NEW correction until a fetch newer than that one succeeds. `null` = trusted.
  const [staleSince, setStaleSince] = useState<number | null>(null);

  // Written as `!(newer)` so a missing `dataUpdatedAt` also counts as stale.
  const receiptStale = staleSince !== null && !(receipt.dataUpdatedAt > staleSince);
  // A new correction must start from the current server receipt: never from errored, stale or
  // still-refreshing data. Retrying inside an already open dialog is not gated (same key).
  const correctionBlocked = receiptStale || receipt.isFetching || receipt.isError;

  const refreshReceipt = async (): Promise<void> => {
    try {
      const result = await receipt.refetch();
      if (!result.isError && result.data !== undefined) setStaleSince(null);
    } catch {
      // The stale gate stays in place; the receipt query surfaces its own error and Retry.
    }
  };

  const openCorrection = (line: StockReceiptLine): void => {
    if (correctionBlocked) return;
    setCorrectionError(null);
    setCorrectionLine(line);
  };

  const submitCorrection = async (input: CorrectReceiptLineInput): Promise<void> => {
    const startedFrom = receipt.dataUpdatedAt;
    setCorrectionError(null);
    try {
      await correct.mutateAsync(input);
      toast.success(t('admin.receiving.correction.applied'));
      setCorrectionLine(null);
    } catch (err) {
      // Explicit failure state (the dialog keeps its key for Retry); the ambiguity is never
      // cleared here. Refetch so the page shows what the server actually holds.
      setCorrectionError(err);
      setStaleSince(startedFrom);
      await refreshReceipt();
    }
  };

  return (
    <main
      dir={dir}
      data-testid="stock-receipt-route"
      className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8"
    >
      <Link
        to="/stock/receiving?tab=receipts"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex min-h-11 items-center gap-1 text-sm"
      >
        <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" />
        {t('admin.receiving.backToList')}
      </Link>
      <header className="mb-6 text-start">
        <h1 className="font-display text-foreground text-[28px] font-bold leading-tight tracking-tight">
          {t('admin.receiving.receipt.title')}
        </h1>
      </header>

      {access.isLoading || (canManage && receipt.isLoading) ? (
        <div className="space-y-3" data-testid="stock-receipt-loading">
          <div className="bg-admin-surface-muted h-40 animate-pulse rounded-lg motion-reduce:animate-none" />
        </div>
      ) : null}

      {/* A failed access request is transient: never treated as "no permission" or "paused". */}
      {access.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="receiving-access-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">{t('common.errors.networkFailed')}</p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void access.refetch()}
            data-testid="receiving-access-retry"
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && !canManage ? <ReceivingNotAvailable /> : null}

      {access.data && canManage && receipt.isError ? (
        <div
          role="alert"
          className="border-border bg-card shadow-admin-card rounded-lg border p-6 text-center"
          data-testid="stock-receipt-error"
        >
          <p className="text-muted-foreground mb-4 text-sm">
            {t(receivingErrorKey(receipt.error), receivingErrorOptions(receipt.error))}
          </p>
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => void refreshReceipt()}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {access.data && canManage && receipt.data ? (
        <div className="space-y-4">
          {!enabled ? <ReceivingPausedBanner correctionsAllowed /> : null}
          <ReceiptDetail
            receipt={receipt.data}
            canCorrect={canManage}
            correctDisabled={correctionBlocked}
            onCorrect={openCorrection}
            onNewLinked={
              enabled
                ? () =>
                    navigate(
                      `/stock/receiving/new?linkedReceiptId=${encodeURIComponent(receipt.data.id)}`,
                    )
                : undefined
            }
          />
        </div>
      ) : null}

      {/* Rendered independently of the receipt query so a refetch (or its failure) never unmounts
          an open session: the form keeps its idempotency key until the merchant closes it. */}
      <CorrectionDialog
        open={correctionLine !== null}
        onOpenChange={(open) => {
          if (!open) setCorrectionLine(null);
        }}
        line={correctionLine}
        onSubmit={submitCorrection}
        isSubmitting={correct.isPending}
        error={correctionError}
      />
    </main>
  );
}

function canManageOf(access: { canManage: boolean } | undefined): boolean {
  return access?.canManage ?? false;
}
